<div class="page">
  @if (loading() && !company()) {
    <app-spinner></app-spinner>
  } @else if (failed()) {
    <div class="state">
      <lib-no-data icon="cloud_off" message="companies.errors.load_one"></lib-no-data>
      <lib-button color="secondary" icon="refresh" [text]="'companies.retry' | translate" (click)="load()"></lib-button>
    </div>
  } @else if (company()) {
    @if (company(); as company) {
    <header class="hero">
      <app-company-logo [name]="company.name" [logo]="company.logo" [size]="72"></app-company-logo>

      <div class="hero-body">
        <div class="hero-title">
          <h1>{{ company.name }}</h1>

          @if (company.allProjects) {
            <span class="badge">{{ 'companies.all_access' | translate }}</span>
          }

          @if (isCurrent()) {
            <span class="badge active">{{ 'companies.active' | translate }}</span>
          }
        </div>

        @if (company.createdDate) {
          <p class="hero-meta">
            {{ 'companies.created_on' | translate: { date: (company.createdDate | date: 'dd/MM/yyyy') } }}
            @if (company.createdBy?.firstName) {
              {{ 'companies.created_by' | translate: { name: company.createdBy!.firstName + ' ' + company.createdBy!.lastName } }}
            }
          </p>
        }
      </div>

      <div class="hero-actions">
        @if (isPrivileged() && !isCurrent()) {
          <lib-button icon="check_circle" [text]="'companies.activate' | translate" (click)="activate()"></lib-button>
        }

        @if (isPrivileged()) {
          <lib-button color="secondary" icon="edit" [text]="'form.edit' | translate" (click)="editing.set(true)"></lib-button>
        }
      </div>
    </header>

    <section class="kpis">
      <div class="kpi">
        <mat-icon>folder</mat-icon>
        <span class="kpi-value">{{ company.projectsCount }}</span>
        <span class="kpi-label">{{ 'companies.projects' | translate }}</span>
      </div>

      <div class="kpi">
        <mat-icon>play_circle</mat-icon>
        <span class="kpi-value">{{ company.allProjects ? '—' : runningCount() }}</span>
        <span class="kpi-label">{{ 'companies.kpi_running' | translate }}</span>
      </div>

      <div class="kpi">
        <mat-icon>sensors</mat-icon>
        <span class="kpi-value">
          @if (company.allProjects) { — } @else { {{ devices().connected }}<small>/{{ devices().count }}</small> }
        </span>
        <span class="kpi-label">{{ 'companies.kpi_devices' | translate }}</span>
      </div>

      <div class="kpi">
        <mat-icon>group</mat-icon>
        <span class="kpi-value">{{ company.usersCount }}</span>
        <span class="kpi-label">{{ 'companies.users' | translate }}</span>
      </div>

      <div class="kpi">
        <mat-icon>category</mat-icon>
        <span class="kpi-value">{{ company.tenants.length }}</span>
        <span class="kpi-label">{{ 'companies.tenants' | translate }}</span>
      </div>
    </section>

    <div class="grid">
      <section class="card wide">
        <div class="card-head">
          <mat-icon>category</mat-icon>
          <h2>{{ 'companies.tenants' | translate }}</h2>
          <span class="card-spacer"></span>

          @if (canManageTenants()) {
            <lib-button icon="add" [text]="'companies.tenant_add' | translate" (click)="openTenant()"></lib-button>
          }
        </div>

        <p class="card-intro">{{ 'companies.tenants_intro' | translate }}</p>

        <div class="tenant-grid">
          @for (tenant of company.tenants; track tenant.id) {
            <article class="tenant-card" [style.--tenant-color]="tenant.color">
              <span class="tenant-icon"><mat-icon>{{ tenant.icon }}</mat-icon></span>

              <div class="tenant-body">
                <h3>{{ tenant.name }}</h3>

                <span class="tenant-count">{{ 'companies.projects_count' | translate: { count: tenant.projects.length } }}</span>

                <span class="tenant-projects">
                  @for (project of tenant.projects; track project.id; let last = $last) {
                    {{ project.name }}{{ last ? '' : ' · ' }}
                  }
                </span>
              </div>

              @if (canManageTenants()) {
                <div class="tenant-actions">
                  <lib-button color="none" textColor="secondary" icon="edit" size="small" [tooltip]="'form.edit' | translate"
                    (click)="openTenant(tenant)">
                  </lib-button>

                  <lib-button color="none" textColor="danger" icon="delete" size="small" [tooltip]="'form.delete' | translate"
                    (click)="removeTenant(tenant)">
                  </lib-button>
                </div>
              }
            </article>
          } @empty {
            <div class="tenant-empty">
              <lib-no-data icon="category" size="small" message="companies.no_tenants"
                [description]="canManageTenants() ? 'companies.no_tenants_desc' : ''">
              </lib-no-data>
            </div>
          }
        </div>
      </section>

      <section class="card wide">
        <div class="card-head">
          <mat-icon>folder</mat-icon>
          <h2>{{ 'companies.projects' | translate }}</h2>
        </div>

        @if (company.allProjects) {
          <p class="implicit">{{ 'companies.all_projects' | translate }}</p>
        } @else if (!projectRows().length) {
          <p class="empty">{{ 'companies.no_projects' | translate }}</p>
        } @else {
          <div class="table-wrap">
            <table class="project-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'companies.col_project' | translate }}</th>
                  <th scope="col">{{ 'companies.col_location' | translate }}</th>
                  <th scope="col">{{ 'companies.col_status' | translate }}</th>
                  <th scope="col">{{ 'companies.col_progress' | translate }}</th>
                  <th scope="col" class="num">{{ 'companies.col_devices' | translate }}</th>
                  <th scope="col">{{ 'companies.col_last_seen' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (row of projectRows(); track row.project.id) {
                  <tr>
                    <td>
                      <span class="project-name">
                        @if (row.project.picture) {
                          <img class="project-picture" [src]="row.project.picture" alt="" />
                        } @else {
                          <mat-icon class="project-picture">folder_open</mat-icon>
                        }
                        <span class="project-text">
                          {{ row.project.name }}
                          @if (row.project.reference) {
                            <small>{{ row.project.reference }}</small>
                          }
                        </span>
                      </span>
                    </td>

                    <td>{{ row.location || '—' }}</td>

                    <td><span [class]="'project-state ' + row.state">{{ 'companies.state_' + row.state | translate }}</span></td>

                    <td>
                      @if (row.progress !== null) {
                        <span class="progress" [attr.aria-label]="row.progress + '%'">
                          <span class="progress-track"><span class="progress-fill" [style.width.%]="row.progress"></span></span>
                          {{ row.progress }}%
                        </span>
                      } @else {
                        —
                      }
                    </td>

                    <td class="num">
                      @if (row.status) {
                        <span class="devices" [matTooltip]="'companies.communication_' + row.status.communication | translate">
                          <span [class]="'dot com-' + row.status.communication"></span>
                          {{ row.status.unitConnected }}/{{ row.status.unitCount }}
                        </span>
                      } @else {
                        —
                      }
                    </td>

                    <td>{{ row.status?.last ? (row.status!.last | date: 'dd/MM/yyyy HH:mm') : '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <!-- What to settle on the portfolio, by kind; the sites concerned in chips -->
      @if (!company.allProjects) {
        <section class="card wide">
          <div class="card-head">
            <mat-icon>notification_important</mat-icon>
            <h2>{{ 'companies.attention' | translate }}</h2>
          </div>

          @if (attentions().length) {
            <div class="attention-list">
              @for (attention of attentions(); track attention.kind) {
                <div [class]="'attention ' + attention.kind">
                  <mat-icon class="attention-icon">{{ attentionIcons[attention.kind] }}</mat-icon>

                  <div class="attention-body">
                    <span class="attention-title">
                      {{ 'companies.attention_' + attention.kind | translate }}
                      <span class="attention-count">{{ attention.rows.length }}</span>
                    </span>
                    <span class="attention-hint">{{ 'companies.attention_' + attention.kind + '_hint' | translate }}</span>

                    <div class="attention-projects">
                      @for (row of attention.rows; track row.project.id) {
                        <span class="attention-project" [matTooltip]="attentionDetail(attention.kind, row)">{{ row.project.name }}</span>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          } @else {
            <p class="all-clear"><mat-icon>check_circle</mat-icon>{{ 'companies.attention_none' | translate }}</p>
          }
        </section>
      }

      <!-- Devices of the live sites: connected now, age of their last data -->
      @if (!company.allProjects) {
        <section class="card">
          <div class="card-head">
            <mat-icon>sensors</mat-icon>
            <h2>{{ 'companies.fleet' | translate }}</h2>
          </div>

          <p class="card-intro">
            {{ 'companies.fleet_summary' | translate: { connected: devices().connected, count: devices().count - archivedDevices() } }}
            @if (archivedDevices()) {
              · {{ 'companies.fleet_archived' | translate: { count: archivedDevices() } }}
            }
          </p>

          @if (fleet().length) {
            <ul class="fleet-list">
              @for (item of fleet(); track item.row.project.id) {
                <li class="fleet-row">
                  <span class="fleet-name" [title]="item.row.project.name">{{ item.row.project.name }}</span>
                  <span class="fleet-bar"><span class="fleet-fill" [style.width.%]="item.connected / item.count * 100"></span></span>
                  <span class="fleet-count">{{ item.connected }}/{{ item.count }}</span>
                  <span [class]="'fleet-last ' + freshness(item.days)"><span class="dot"></span>{{ lastData(item.days) }}</span>
                </li>
              }
            </ul>
          } @else {
            <p class="empty">{{ 'companies.fleet_empty' | translate }}</p>
          }
        </section>
      }

      <section class="card">
        <div class="card-head">
          <mat-icon>group</mat-icon>
          <h2>{{ 'companies.team' | translate }}</h2>
        </div>

        @if (company.allProjects) {
          <p class="implicit">{{ 'companies.all_users' | translate }}</p>
        } @else {
          @if (roles().length) {
            <div class="role-chips">
              @for (item of roles(); track item.role) {
                <span class="role-chip">{{ item.role }} <strong>{{ item.count }}</strong></span>
              }
            </div>
          }

          <ul class="item-list">
            @for (user of company.users; track user.id) {
              <li>
                <app-user-avatar [avatarUrl]="user.avatar" [firstName]="user.firstName" [lastName]="user.lastName" [size]="32">
                </app-user-avatar>

                <span class="user">
                  {{ user.firstName }} {{ user.lastName }}
                  @if (user.email) {
                    <small>{{ user.email }}</small>
                  }
                </span>

                @if (user.role) {
                  <span class="role-tag">{{ user.role.name }}</span>
                }

                @if (canManageTenants()) {
                  <mat-icon class="sso" [class.off]="!isSsoActive(user)"
                    [matTooltip]="(isSsoActive(user) ? 'companies.sso_on' : 'companies.sso_off') | translate">
                    {{ isSsoActive(user) ? 'verified_user' : 'gpp_maybe' }}
                  </mat-icon>
                }
              </li>
            } @empty {
              <li class="empty">{{ 'companies.no_users' | translate }}</li>
            }
          </ul>
        }
      </section>

      @if (!company.allProjects) {
        <!-- Who uses Unifield: last connection of the client accounts (CAD.42 staff left out), for those who manage -->
        @if (canManageTenants()) {
          <section class="card">
            <div class="card-head">
              <mat-icon>insights</mat-icon>
              <h2>{{ 'companies.adoption' | translate }}</h2>
            </div>

            @if (adoption().total) {
              <p class="adoption-figure">
                <strong>{{ adoption().activeRate }} %</strong>
                {{ 'companies.adoption_active' | translate: { count: adoption().active, total: adoption().total } }}
              </p>

              <span class="adoption-bar">
                @for (bucket of adoption().buckets; track bucket.key) {
                  @if (bucket.count) {
                    <span [class]="'segment ' + bucket.key" [style.flex-grow]="bucket.count"
                      [matTooltip]="('companies.adoption_' + bucket.key | translate) + ' : ' + bucket.count"></span>
                  }
                }
              </span>

              <ul class="adoption-legend">
                @for (bucket of adoption().buckets; track bucket.key) {
                  <li>
                    <span [class]="'dot ' + bucket.key"></span>
                    <span class="legend-label">{{ 'companies.adoption_' + bucket.key | translate }}</span>
                    <strong>{{ bucket.count }}</strong>
                  </li>
                }
              </ul>

              <div class="adoption-checks">
                <span class="check-chip" [class.alert]="adoption().dormant">
                  <mat-icon>person_off</mat-icon>
                  {{ 'companies.adoption_review' | translate: { count: adoption().dormant } }}
                </span>
                <span class="check-chip">
                  <mat-icon>person_outline</mat-icon>
                  {{ 'companies.adoption_guests' | translate: { count: adoption().guests } }}
                </span>
                @if (adoption().unverified) {
                  <span class="check-chip">
                    <mat-icon>mark_email_unread</mat-icon>
                    {{ 'companies.adoption_unverified' | translate: { count: adoption().unverified } }}
                  </span>
                }
              </div>
            } @else {
              <p class="empty">{{ 'companies.no_users' | translate }}</p>
            }
          </section>
        }

        <!-- Reports really sent by each site: per frequency, and to how many people -->
        <section class="card">
          <div class="card-head">
            <mat-icon>forward_to_inbox</mat-icon>
            <h2>{{ 'companies.diffusion' | translate }}</h2>
          </div>

          @if (diffusion().length) {
            <p class="card-intro">{{ 'companies.diffusion_summary' | translate: { count: diffusionOn(), total: diffusion().length } }}</p>

            <ul class="diffusion-list">
              <li class="diffusion-row diffusion-head">
                <span></span>
                <span>{{ 'companies.diffusion_daily' | translate }}</span>
                <span>{{ 'companies.diffusion_weekly' | translate }}</span>
                <span>{{ 'companies.diffusion_monthly' | translate }}</span>
                <span [matTooltip]="'companies.diffusion_recipients' | translate"><mat-icon>group</mat-icon></span>
                <span></span>
              </li>
              @for (item of diffusion(); track item.row.project.id) {
                <li class="diffusion-row" [class.muted]="item.state === 'off'">
                  <span class="fleet-name" [title]="item.row.project.name">{{ item.row.project.name }}</span>
                  @for (count of [item.reports.daily, item.reports.weekly, item.reports.monthly, item.reports.recipients]; track $index) {
                    <span class="diffusion-count" [class.zero]="!count">{{ count }}</span>
                  }
                  @if (item.state !== 'on') {
                    <span [class]="'diffusion-state ' + item.state">{{ 'companies.diffusion_' + item.state | translate }}</span>
                  } @else {
                    <span></span>
                  }
                </li>
              }
            </ul>
          } @else {
            <p class="empty">{{ 'companies.diffusion_empty' | translate }}</p>
          }
        </section>

        <!-- Services switched on: AI modules by number of sites, stream played over 30 days -->
        <section class="card">
          <div class="card-head">
            <mat-icon>auto_awesome</mat-icon>
            <h2>{{ 'companies.services' | translate }}</h2>
          </div>

          <h3 class="sub-head">{{ 'companies.services_ai' | translate }}</h3>
          @if (services().modules.length) {
            <p class="card-intro">{{ 'companies.services_ai_summary' | translate: { count: aiCovered(), total: diffusion().length } }}</p>
            <div class="adoption-checks">
              @for (module of services().modules; track module.name) {
                <span class="check-chip" [matTooltip]="moduleSites(module)">
                  <mat-icon>smart_toy</mat-icon>
                  {{ moduleName(module.name) }} <strong>{{ module.rows.length }}</strong>
                </span>
              }
            </div>
          } @else {
            <p class="empty">{{ 'companies.services_ai_none' | translate }}</p>
          }

          <h3 class="sub-head">{{ 'companies.services_stream' | translate }}</h3>
          @if (services().streams.length) {
            <ul class="fleet-list">
              @for (item of services().streams; track item.row.project.id) {
                <li class="stream-row">
                  <span class="fleet-name" [title]="item.row.project.name">{{ item.row.project.name }}</span>
                  <span class="fleet-bar"><span class="fleet-fill" [style.width.%]="item.share"></span></span>
                  <span class="stream-value">
                    {{ duration(item.played) }}
                    @if (item.dailyLimit) {
                      <small>{{ 'companies.services_daily_limit' | translate: { limit: duration(item.dailyLimit) } }}</small>
                    }
                  </span>
                </li>
              }
            </ul>
          } @else {
            <p class="empty">{{ 'companies.services_stream_none' | translate }}</p>
          }
        </section>

        <!-- Sites not archived on the map, coloured by state; the ones without a position listed apart -->
        <section class="card">
          <div class="card-head">
            <mat-icon>map</mat-icon>
            <h2>{{ 'companies.sites_map' | translate }}</h2>
          </div>

          <div class="sites-map">
            @defer (on viewport) {
              <google-map appMapTheme [options]="mapOptions" width="100%" height="100%" (mapInitialized)="fitSites($event)">
                @for (pin of sites().pins; track pin.row.project.id) {
                  <map-marker [position]="{ lat: pin.lat, lng: pin.lng }" [options]="pinOptions()[pin.row.project.id]!"
                    (mapMouseover)="hoverPin(pin, $event)" (mapMouseout)="hoveredPin.set(null)"></map-marker>
                }
              </google-map>
            } @placeholder {
              <span></span>
            }

            @if (hoveredPin(); as hovered) {
              <lib-tooltip [x]="hovered.x" [y]="hovered.y">
                <strong>{{ hovered.pin.row.project.name }}</strong>
                @if (hovered.pin.row.location) {
                  <span class="tooltip-detail">{{ hovered.pin.row.location }}</span>
                }
                <span class="tooltip-detail">{{ 'companies.state_' + hovered.pin.state | translate }}</span>
              </lib-tooltip>
            }
          </div>

          <div class="adoption-checks">
            @for (state of pinStates; track state) {
              <span class="check-chip"><span [class]="'pin-dot ' + state"></span>{{ 'companies.state_' + state | translate }} <strong>{{ pinCounts()[state] }}</strong></span>
            }
            @if (sites().unplaced.length) {
              <span class="check-chip alert" [matTooltip]="unplacedNames()">
                <mat-icon>location_off</mat-icon>
                {{ 'companies.sites_unplaced' | translate: { count: sites().unplaced.length } }}
              </span>
            }
          </div>
        </section>

        <!-- Portfolio timeline: the sites not archived on one period, dates left as created flagged -->
        @if (timeline().rows.length) {
          <section class="card wide">
            <div class="card-head">
              <mat-icon>view_timeline</mat-icon>
              <h2>{{ 'companies.timeline' | translate }}</h2>
              <span class="card-spacer"></span>
              @if (guessedNames().length) {
                <span class="check-chip alert" [matTooltip]="guessedNames().join(', ')">
                  <mat-icon>event_busy</mat-icon>
                  {{ 'companies.timeline_guessed' | translate: { count: guessedNames().length } }}
                </span>
              }
            </div>

            <div class="timeline" [style.--today]="timeline().today === null ? null : timeline().today + '%'">
              <div class="timeline-row timeline-axis">
                <span></span>
                <span class="timeline-track">
                  @for (tick of timeline().ticks; track tick.label) {
                    <span class="timeline-tick" [style.left.%]="tick.left">{{ tick.label }}</span>
                  }
                </span>
              </div>
              @for (item of timeline().rows; track item.row.project.id) {
                <div class="timeline-row">
                  <span class="fleet-name" [title]="item.row.project.name">{{ item.row.project.name }}</span>
                  <span class="timeline-track">
                    <span [class]="'timeline-bar ' + item.row.state" [class.guessed]="item.guessed"
                      [style.left.%]="item.left" [style.width.%]="item.width" [matTooltip]="period(item)"></span>
                  </span>
                </div>
              }
            </div>
          </section>
        }
      }

    </div>
    }
  } @else {
    <div class="state">
      <lib-no-data icon="domain_disabled" message="companies.none" description="companies.none_desc"></lib-no-data>
    </div>
  }
</div>

@if (editing() && company(); as company) {
  <app-company-drawer [companyId]="company.id" (closed)="editing.set(false)"></app-company-drawer>
}
